<script lang="ts" setup>
const { getContinentAttributes, getMetaAttributes } = useApiAttributes()
const { t } = useI18n()
const api = useApi()

const { data: response, execute, status } = useAsyncData(
  'doc:locations-search-countries',
  () => api.locations.countries.fetch(),
  { immediate: false },
)

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/locations/countries',
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.page'),
    name: 'page',
    optional: true,
    type: 'number',
  },
  {
    description: t('attributes.search'),
    name: 'search',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.size'),
    name: 'size',
    optional: true,
    type: 'number',
  },
]

const responseAttributes: ApiAttribute[] = [
  {
    description: t('response.countries'),
    name: 'countries',
    type: 'array',

    attributes: [
      {
        description: t('response.code'),
        name: 'code',
        parent: 'country',
        type: 'string',
      },
      {
        attributes: getContinentAttributes({ parent: 'country' }),
        description: t('response.continent'),
        name: 'continent',
        parent: 'country',
        type: 'object',
      },
      {
        description: t('response.latitude'),
        name: 'latitude',
        parent: 'country',
        type: 'string',
      },
      {
        description: t('response.longitude'),
        name: 'longitude',
        parent: 'country',
        type: 'string',
      },
      {
        description: t('response.name'),
        name: 'name',
        parent: 'country',
        type: 'string',
      },
      {
        description: t('response.nameEs'),
        name: 'nameEs',
        parent: 'country',
        type: 'string',
      },
      {
        description: t('response.nameFr'),
        name: 'nameFr',
        parent: 'country',
        type: 'string',
      },
      {
        description: t('response.nameNative'),
        name: 'nameNative',
        parent: 'country',
        type: 'string',
      },
      {
        description: t('response.population'),
        name: 'population',
        parent: 'country',
        type: 'string',
      },
    ],
  },
  getMetaAttributes(),
]

async function onVisibilityChange(visibility: boolean) {
  if (visibility && !response.value) {
    await execute()
  }
}
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "page": "The page to fetch (default to 1).",
      "search": "An additional search query applied to the country name.",
      "size": "The number of countries to be returned (between 1 and 20). Default to 10."
    },
    "response": {
      "code": "The country code.",
      "continent": "The country's continent.",
      "countries": "The countries that match your conditions.",
      "latitude": "The country's latitude.",
      "longitude": "The country's longitude.",
      "name": "The country's name in english.",
      "nameEs": "The country's name in spanish.",
      "nameFr": "The country's name in french.",
      "nameNative": "The country's name in its native language.",
      "population": "The country's population."
    }
  },
  "es": {
    "attributes": {
      "page": "La página a recuperar (por defecto 1).",
      "search": "Una consulta de búsqueda adicional aplicada al nombre del país.",
      "size": "El número de países a devolver (entre 1 y 20). Por defecto 10."
    },
    "response": {
      "code": "El código del país.",
      "continent": "El continente del país.",
      "countries": "Los países que coinciden con tus criterios.",
      "latitude": "La latitud del país.",
      "longitude": "La longitud del país.",
      "name": "El nombre del país en inglés.",
      "nameEs": "El nombre del país en español.",
      "nameFr": "El nombre del país en francés.",
      "nameNative": "El nombre del país en su idioma nativo.",
      "population": "La población del país."
    }
  },
  "fr": {
    "attributes": {
      "page": "La page à récupérer (par défaut 1).",
      "search": "Une recherche additionnelle appliquée au nom du pays.",
      "size": "Le nombre de pays à retourner (entre 1 et 20). Par défaut 10."
    },
    "response": {
      "code": "Le code du pays.",
      "continent": "Le continent du pays.",
      "countries": "Les pays qui correspondent à vos critères.",
      "latitude": "La latitude du pays.",
      "longitude": "La longitude du pays.",
      "name": "Le nom du pays en anglais.",
      "nameEs": "Le nom du pays en espagnol.",
      "nameFr": "Le nom du pays en français.",
      "nameNative": "Le nom du pays dans sa langue d'origine.",
      "population": "La population du pays."
    }
  }
}
</i18n>
